import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useMember, formatApiError } from "../lib/memberAuth";
import { SocialButtons } from "../components/account/SocialButtons";
import { CHURCH } from "../lib/data";

const inp = "w-full border border-black/15 rounded-xl px-4 py-3 text-sm focus:border-[#E63946] outline-none";
const L = ({ children }) => <label className="block text-xs uppercase tracking-[0.18em] text-black/50 mb-2">{children}</label>;

export default function MemberAuth() {
  const { setMember } = useMember();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [f, setF] = useState({ name: "", email: "", password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("error") === "apple") toast.error("Apple sign-in failed. Please try again or use another method.");
    // eslint-disable-next-line
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/members/login", { email: f.email, password: f.password });
        setMember(data); navigate("/account");
      } else if (mode === "register") {
        const { data } = await api.post("/members/register", { name: f.name, email: f.email, password: f.password });
        setMember(data); toast.success("Account created! Check your email for a verification code.");
        navigate("/account");
      } else if (mode === "forgot") {
        await api.post("/members/forgot-password", { email: f.email });
        toast.success("If that email has an account, a reset code is on the way.");
        setMode("reset");
      } else {
        await api.post("/members/reset-password", { email: f.email, code: f.code, password: f.password });
        toast.success("Password reset! You can sign in now.");
        setMode("login");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong");
    }
    setBusy(false);
  };

  return (
    <div className="pt-[72px] min-h-screen bg-[#FAFAFA] grid place-items-center px-5 py-16" data-testid="member-auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={CHURCH.logo} alt="One Love Ministries" className="w-16 h-16 object-contain mx-auto mb-4" />
          <h1 className="font-display font-extrabold text-3xl">
            {mode === "login" ? "Welcome Back" : mode === "register" ? "Create Your Account" : mode === "forgot" ? "Forgot Password" : "Reset Password"}
          </h1>
          <p className="text-black/50 text-sm mt-2 flex items-center justify-center gap-1.5"><HeartHandshake size={15} className="text-[#E63946]" /> Your One Love Ministries Member Portal</p>
        </div>
        {(mode === "login" || mode === "register") && (
          <>
            <SocialButtons intent="login" />
            <div className="flex items-center gap-3 text-xs text-black/35 my-5">
              <span className="h-px bg-black/10 flex-1" /> OR CONTINUE WITH EMAIL <span className="h-px bg-black/10 flex-1" />
            </div>
          </>
        )}
        <form onSubmit={submit} className="bg-white border border-black/10 rounded-2xl p-7 space-y-4">
          {mode === "register" && <div><L>Full Name</L><input className={inp} required value={f.name} onChange={set("name")} data-testid="auth-name-input" /></div>}
          <div><L>Email</L><input type="email" className={inp} required value={f.email} onChange={set("email")} data-testid="auth-email-input" /></div>
          {mode === "reset" && <div><L>6-Digit Code (from your email)</L><input className={inp} required value={f.code} onChange={set("code")} data-testid="auth-code-input" /></div>}
          {mode !== "forgot" && (
            <div><L>{mode === "reset" ? "New Password" : "Password"}</L>
              <input type="password" className={inp} required minLength={8} value={f.password} onChange={set("password")} data-testid="auth-password-input" />
              {mode !== "login" && <p className="text-xs text-black/40 mt-1.5">At least 8 characters</p>}
            </div>
          )}
          <button type="submit" disabled={busy} data-testid="auth-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E63946] text-white py-3.5 font-medium hover:bg-[#BA1B25] transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : mode === "forgot" ? "Send Reset Code" : "Reset Password"}
          </button>
          <div className="text-center text-sm text-black/50 space-y-1.5 pt-1">
            {mode === "login" && (
              <>
                <p>New here? <button type="button" onClick={() => setMode("register")} data-testid="switch-register-btn" className="text-[#E63946] font-medium hover:underline">Create an account</button></p>
                <p><button type="button" onClick={() => setMode("forgot")} data-testid="forgot-password-btn" className="hover:underline">Forgot your password?</button></p>
              </>
            )}
            {mode === "register" && <p>Already have an account? <button type="button" onClick={() => setMode("login")} className="text-[#E63946] font-medium hover:underline">Sign in</button></p>}
            {(mode === "forgot" || mode === "reset") && <p><button type="button" onClick={() => setMode("login")} className="hover:underline">Back to sign in</button></p>}
          </div>
        </form>
        <p className="text-center text-xs text-black/40 mt-4">By creating an account, you agree to the One Love Ministries Terms of Use and Privacy Policy. Google or Apple sign-in only shares your name and verified email — we never receive your Google or Apple password.</p>
        <p className="text-center text-xs text-black/40 mt-3">No account needed to give, buy tickets, or shop — <Link to="/give" className="underline">guest checkout is always available</Link>.</p>
      </div>
    </div>
  );
}
